import Modifier from 'ember-modifier';

import { registerDestructor } from '@ember/destroyable';
import autoAnimate from '@formkit/auto-animate';
import { getTransitionSizes } from '@formkit/auto-animate';

import type { ArgsFor, PositionalArgs } from 'ember-modifier';
import type {
  AnimationController,
  AutoAnimateOptions,
  AutoAnimationPlugin
} from '@formkit/auto-animate';
import type Owner from '@ember/owner';

export type AutoAnimateModifierOptions =
  | Partial<AutoAnimateOptions>
  | AutoAnimationPlugin
  | 'vertical';

interface IAutoAnimateSignature {
  Element: HTMLElement;
  Args: {
    Named: Record<string, never>;
    Positional: [animate?: boolean, options?: AutoAnimateModifierOptions];
  };
}

export const vertical: AutoAnimationPlugin = (el, action, oldCoords, newCoords) => {
  let keyframes: Keyframe[] = [];
  // supply a different set of keyframes for each action
  if (action === 'add') {
    keyframes = [{ opacity: 0 }, { opacity: 1 }];
  }
  // keyframes can have as many "steps" as you prefer
  // and you can use the 'offset' key to tune the timing
  if (action === 'remove') {
    keyframes = [{ opacity: 1 }, { opacity: 0 }];
  }

  if (action === 'remain') {
    // for items that remain, calculate the delta
    // from their old position to their new position
    const deltaX = oldCoords!.left - newCoords!.left;
    const deltaY = oldCoords!.top - newCoords!.top;
    // use the getTransitionSizes() helper function to
    // get the old and new widths of the elements
    const [widthFrom, widthTo, heightFrom, heightTo] = getTransitionSizes(
      el,
      oldCoords!,
      newCoords!
    );
    const start: Record<string, any> = {
      transform: `translate(${deltaX}px, ${deltaY}px)`
    };
    const end: Record<string, any> = {
      transform: `translate(0, 0)`
    };
    if (widthFrom !== widthTo) {
      start['width'] = `${widthFrom}px`;
      end['width'] = `${widthTo}px`;
    }
    if (heightFrom !== heightTo) {
      start['height'] = `${heightFrom}px`;
      end['height'] = `${heightTo}px`;
    }
    keyframes = [start, end];
  }
  // return our KeyframeEffect() and pass
  // it the chosen keyframes.
  return new KeyframeEffect(el, keyframes, { duration: 250, easing: 'ease-in-out' });
};

export default class AutoAnimateModifier extends Modifier<IAutoAnimateSignature> {
  controller: AnimationController | undefined;

  constructor(owner: Owner, args: ArgsFor<IAutoAnimateSignature>) {
    super(owner, args);

    registerDestructor(this, () => {
      this.controller = undefined;
    });
  }

  modify(element: Element | null, [enabled, options]: PositionalArgs<IAutoAnimateSignature>) {
    if (element instanceof HTMLElement) {
      this.controller = autoAnimate(element, options === 'vertical' ? vertical : options);

      if (enabled) {
        this.controller.enable();
      } else {
        this.controller.disable();
      }
    } else {
      this.controller = undefined;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    autoanimate: typeof AutoAnimateModifier;
  }
}
